import InspectionStatus from '@/enums/InspectionStatus.enum';
import { isInspectionStatus } from '@/utils/inspectionStart.utils';

export const PACKING_LIST_IMAGE_TYPE = 'Packing List';
export const NO_PACKING_LIST_COMMENT = 'No packing list available';
export const QUANTITY_FAIL_DEFECT_KEY = 'quantity-fail-defect';
const CRITICAL_SEVERITY_NAME = 'Critical';
const POSITIVE_RELEASE_VALUE = 4;
const QUANTITY_LOCKED_STATUSES = [InspectionStatus.FINISHED, InspectionStatus.CLOSED] as const;

/**
 * Returns true when the inspection is Finished or Closed, so the actual quantity can no longer change.
 */
export const isActualQuantityLocked = (inspectionStatusId: string | undefined): boolean =>
  QUANTITY_LOCKED_STATUSES.some((status) => isInspectionStatus(inspectionStatusId, status));

export interface AqlLevelRow {
  inspectionLevel: number;
  qtyMin: number;
  qtyMax: number;
  sampleQty: number;
  major?: number | null;
  minor?: number | null;
  aql0?: number | null;
  aql0d4?: number | null;
  aql0d65?: number | null;
  aql1?: number | null;
  aql1d5?: number | null;
  aql2d5?: number | null;
  aql4?: number | null;
  aql6d5?: number | null;
}

export interface RecalculatedSampling {
  sampleSize: number;
  allowedDefectsMaj: number;
  allowedDefectsMin: number;
}

const AQL_VALUE_TO_FIELD: Record<string, keyof AqlLevelRow> = {
  major: 'major',
  minor: 'minor',
  '0': 'aql0',
  '0.4': 'aql0d4',
  '0.65': 'aql0d65',
  '1.0': 'aql1',
  '1.5': 'aql1d5',
  '2.5': 'aql2d5',
  '4.0': 'aql4',
  '6.5': 'aql6d5',
};

export function hasEnteredActualQuantity(value: number | null | undefined): value is number {
  if (value == null) {
    return false;
  }
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity > 0 && Number.isInteger(quantity);
}

export function isActualQuantityWithinTolerance(
  advisedQuantity: number,
  actualQuantity: number,
  overPercent: number,
  underPercent: number
): boolean {
  const upperBound = advisedQuantity * (1 + overPercent / 100);
  const lowerBound = advisedQuantity * (1 - underPercent / 100);
  return actualQuantity >= lowerBound && actualQuantity <= upperBound;
}

export function hasQuantityTolerances(
  overPercent: number | null | undefined,
  underPercent: number | null | undefined
): boolean {
  return (
    overPercent != null &&
    underPercent != null &&
    Number.isFinite(Number(overPercent)) &&
    Number.isFinite(Number(underPercent))
  );
}

export type QuantityEntryPrompt = 'none' | 'discrepancy' | 'out-of-tolerance';

/**
 * Chooses the dialog shown after the inspector enters an actual quantity.
 * The packing-list discrepancy dialog is only for a difference inside tolerance.
 * A quantity outside the allowable range opens the out-of-tolerance dialog directly.
 */
export function resolveQuantityEntryPrompt(input: {
  advisedQuantity: number;
  actualQuantity: number | null;
  overPercent: number | null | undefined;
  underPercent: number | null | undefined;
}): QuantityEntryPrompt {
  if (!hasEnteredActualQuantity(input.actualQuantity)) {
    return 'none';
  }
  if (input.actualQuantity === input.advisedQuantity) {
    return 'none';
  }
  if (!hasQuantityTolerances(input.overPercent, input.underPercent)) {
    return 'discrepancy';
  }
  const withinTolerance = isActualQuantityWithinTolerance(
    input.advisedQuantity,
    input.actualQuantity,
    Number(input.overPercent),
    Number(input.underPercent)
  );
  return withinTolerance ? 'discrepancy' : 'out-of-tolerance';
}

/**
 * Skip is only offered when the discrepancy dialog is shown without a tolerance range.
 * A quantity inside the allowed range must record packing-list information or its absence.
 */
export function shouldShowSkipQuantityDiscrepancy(withinTolerance: boolean): boolean {
  return !withinTolerance;
}

/**
 * Failing an out-of-tolerance inspection always logs a Critical defect.
 */
export function findCriticalSeverity<T extends { name: string }>(
  severities: readonly T[]
): T | undefined {
  return severities.find((severity) => severity.name === CRITICAL_SEVERITY_NAME);
}

export function countCriticalDefects(
  defects: readonly { defectSeverity?: string | null }[] | undefined
): number {
  return (defects ?? []).filter((defect) => defect.defectSeverity === CRITICAL_SEVERITY_NAME)
    .length;
}

function readAllowedDefects(
  row: AqlLevelRow,
  aqlValue: string,
  fallback: 'major' | 'minor'
): number | null {
  const field = AQL_VALUE_TO_FIELD[aqlValue] ?? fallback;
  const allowed = row[field];
  if (typeof allowed !== 'number') {
    return null;
  }
  return allowed;
}

export function findAqlLevelRow(
  rows: AqlLevelRow[],
  inspectionLevelValue: number,
  quantity: number
): AqlLevelRow | null {
  return (
    rows.find(
      (row) =>
        row.inspectionLevel === inspectionLevelValue &&
        quantity >= row.qtyMin &&
        quantity <= row.qtyMax
    ) ?? null
  );
}

export function resolveDeviceSampling(input: {
  inspectionLevelValue: number | null | undefined;
  actualQuantity: number;
  aqlRows: AqlLevelRow[];
  aqlMajor?: string | null;
  aqlMinor?: string | null;
  currentAllowedMajor: number;
  currentAllowedMinor: number;
}): RecalculatedSampling | null {
  if (input.inspectionLevelValue === POSITIVE_RELEASE_VALUE) {
    return {
      sampleSize: input.actualQuantity,
      allowedDefectsMaj: input.currentAllowedMajor,
      allowedDefectsMin: input.currentAllowedMinor,
    };
  }
  if (input.inspectionLevelValue == null || !input.aqlMajor || !input.aqlMinor) {
    return null;
  }
  const row = findAqlLevelRow(input.aqlRows, input.inspectionLevelValue, input.actualQuantity);
  if (!row) {
    return null;
  }
  const allowedDefectsMaj = readAllowedDefects(row, input.aqlMajor, 'major');
  const allowedDefectsMin = readAllowedDefects(row, input.aqlMinor, 'minor');
  if (allowedDefectsMaj == null || allowedDefectsMin == null) {
    return null;
  }
  return {
    sampleSize: row.sampleQty,
    allowedDefectsMaj,
    allowedDefectsMin,
  };
}

export function appendNoPackingListComment(comment: string | undefined): string {
  const existing = comment?.trim() ?? '';
  if (existing.includes(NO_PACKING_LIST_COMMENT)) {
    return existing;
  }
  return existing.length > 0 ? `${existing}\n${NO_PACKING_LIST_COMMENT}` : NO_PACKING_LIST_COMMENT;
}

export function removeNoPackingListComment(comment: string | undefined): string {
  if (!comment) {
    return '';
  }
  return comment
    .split('\n')
    .filter((line) => line.trim() !== NO_PACKING_LIST_COMMENT)
    .join('\n')
    .trim();
}
